import { useEffect } from 'react';

import { accountPageSlugs } from '@boss/constants/b2b-b2c';
import { useRouter } from '@boss/hooks';

import {
  ACCOUNT_TAB_PERMISSIONS,
  type PermissionRequirement,
  filterBlockedAccountPages,
  isBlocked,
} from './permissions';
import { isB2b } from '../../utils';
import { useContactPermissions } from '../use-contact-permissions/use-contact-permissions';
import { useProfile } from '../use-profile/use-profile';

export type { PermissionRequirement };
export { ACCOUNT_TAB_PERMISSIONS, filterBlockedAccountPages };

export type PermissionGuard = { pending: boolean; blocked: boolean };

export const usePermissionGuard = (requirement: PermissionRequirement | undefined): PermissionGuard => {
  const { isLoggedIn, status } = useProfile();
  const { replace, locale } = useRouter();
  // Destructure with defaults for the colora build (hook returns undefined there);
  // those values are never used — the effect returns early when !isB2b.
  const {
    showprice = true,
    canorder = true,
    loading: permissionsLoading = false,
  } = useContactPermissions(locale) ?? {};

  useEffect(() => {
    // [39787] RBAC is B2B-only — never redirect or bounce B2C visitors.
    if (!isB2b || requirement === undefined) {
      return;
    }
    // Only redirect once the profile is definitively resolved. 'pristine'/'loading' means
    // the profile may still be settling (e.g. mid-login in the B2C code flow) — wait, don't bounce.
    if (status !== 'idle') {
      return;
    }
    if (!isLoggedIn) {
      replace(`/${locale}`);
      return;
    }
    if (permissionsLoading) {
      return;
    }
    if (isBlocked(requirement, showprice, canorder)) {
      replace(`/${locale}/${accountPageSlugs[locale]}`);
    }
  }, [requirement, isLoggedIn, status, showprice, canorder, permissionsLoading, locale, replace]);

  // [39787] Render gate: while the profile/permissions are still resolving, or once the
  // route turns out to be blocked, the page must not paint anything — the effect above
  // handles navigation. B2C and pages without a requirement render normally (fail-open).
  const gated = isB2b && requirement !== undefined;
  const pending = gated && (status !== 'idle' || permissionsLoading);
  const blocked = gated && status === 'idle' && (!isLoggedIn || isBlocked(requirement, showprice, canorder));

  return { pending, blocked };
};
